import { MaskTypes, CookieType, HttpStatusCode, App, SocketEvent, CoverImageClassroomCount, UserType } from './constant'
import { ElLoading } from 'element-plus'
import i18n from '../i18n'
import { createApp } from 'vue'
import { setupPlugins } from '../utils/setup-plugins'
import enumeration, { SchoolLevel } from '@/common/enumeration'
import notify from '../utils/notify'
import { apis, getApiUrl } from '@/apis/business/api-config'
import { removeAuthToken } from '@/utils/auth'
import idb from '@/utils/idb'
import { isUuid, emptyUuid } from '@/utils/uuid'
import { getContextData, ContextDataKey } from '@/utils/context-data'
import { encode } from 'html-entities'
import { getEnvConfig } from '@/utils/env-variables'
import { emit } from '@/utils/event-bus'
import ExportAPI from '@/apis/business/common/export-api.js'
import type { SocketData, File, RoleUser } from './entity.types'
import element from '@/plugins/element'
import { formatNumber } from '@/utils/number'
import { localStorageKeys, sessionStorageKeys } from '@/common/storage-keys'

import { localStorage, sessionStorage } from '@/utils/storage'
import { TeacherRankingConfig } from '@/utils/cache-data'

/**
 * Class chứa các function dùng chung cho hệ thống phía client
 */

const getI18n = function (key: string, params?: Record<string, unknown>) {
  return params ? i18n?.global?.t(key, params) : i18n?.global?.t(key)
}

/**
 * @description Focus vào các ô input, area, select đầu tiên của container
 * @param {*} container
 * @author PHNam
 */
const focusFirstControl = (container: HTMLElement) => {
  const controls = container.querySelectorAll(
    `input[type="text"]:not([disabled]):not([tabindex="-1"]):not([readonly]),
    textarea:not([disabled]):not([tabindex="-1"]):not([readonly]),
    select:not([disabled]):not([tabindex="-1"]):not([readonly])`
  )

  for (let index = 0; index < controls.length; index++) {
    const element = controls[index]
    const elementStyle = window.getComputedStyle(element)

    const isPlaceOnDocument =
      // @ts-ignore
      element.offsetWidth > 0 && element.offsetHeight > 0
    const isVisible =
      elementStyle.visibility !== 'hidden' && elementStyle.display !== 'none'

    if (isPlaceOnDocument && isVisible) {
      // @ts-ignore
      element.focus()
      break
    }
  }
}

/**
 * @description Redirect sang app khác
 * @param {String} app
 * @param {String} path
 */
const redirectToApp = (app: string, path?: string) => {
  let href = getLinkToApp(app)

  if (path) {
    href += `/${path}`
  }

  window.location.href = href
}

/**
 * @description Lấy đường dẫn đếp app tương ứng
 * @param app
 * @returns
 * @author PHNam
 */
const getLinkToApp = (app: string) => {
  const origin = window.location.origin
  // @ts-ignore
  const appPath = window.__env.app[app]
  const href = `${origin}/${appPath}`

  return href
}
/**
 * @description Kiểm tra ứng dụng có đang được sử dụng trên tablet hay không
 * @returns
 * @author PHNam
 */
const isTablet = () => {
  // const userAgent = navigator.userAgent.toLowerCase()
  // const isTablet = /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(
  //   userAgent
  // )
  const desktopMinWidthHeight = 1280
  const isTouchDevice = window.navigator.maxTouchPoints > 0
  const isTabletOrMobileResolution =
    window.screen.availHeight < desktopMinWidthHeight &&
    window.screen.availWidth < desktopMinWidthHeight
  const isTablet = isTouchDevice && isTabletOrMobileResolution

  return isTablet
}

const destrucSchoolLevel = (schoolLevel: number): number[] => {
  let schoolLevels = [schoolLevel]

  switch (schoolLevel) {
    case SchoolLevel.PrimaySecondSchool:
      schoolLevels = [SchoolLevel.PrimarySchool, SchoolLevel.SecondarySchool]
      break
    case SchoolLevel.SecondaryHighSchool:
      schoolLevels = [SchoolLevel.SecondarySchool, SchoolLevel.HighSchool]
      break
    case SchoolLevel.PrimaySecondHighSchool:
      schoolLevels = [
        SchoolLevel.PrimarySchool,
        SchoolLevel.SecondarySchool,
        SchoolLevel.HighSchool
      ]
      break
  }

  return schoolLevels
}

/**
 * Đặt giá trị cho cookie
 * @param {} cname
 * @param {*} cvalue
 * @param {*} exdays
 */
const setCookie = (cname: string, cvalue: any, exdays: number) => {
  const date = new Date()

  date.setTime(date.getTime() + exdays * 24 * 60 * 60 * 1000)
  const expires = 'expires=' + date.toUTCString()

  document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/'
}

/**
 * Lấy giá trị cookie
 * @param {*} cname
 * @returns
 */
const getCookie = (cname: string) => {
  const name = cname + '='
  const decodedCookie = decodeURIComponent(document.cookie)
  const ca = decodedCookie.split(';')

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]

    while (c.charAt(0) === ' ') {
      c = c.substring(1)
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length)
    }
  }
  return ''
}

/**
 * @description Xử lý khi có lỗi xảy ra ở axios
 * @param {*} error
 * @author PHNam
 */
const handleNetworkError = (
  error: Error | any,
  handleCustomError?: Function
) => {
  const isAxiosError = error.isAxiosError
  const initMessage = isAxiosError ? null : error.UserMessage
  const statusCode = isAxiosError ? error?.response?.status : error.Code
  let showNotify = true
  let message = ''

  if (error?.message === 'Network Error') {
    notify.showError({
      message: getI18n('ErrorMessage.NetworkError')
    })
  } else {
    switch (statusCode) {
      case HttpStatusCode.BadRequest:
        message = getI18n('ErrorMessage.BadRequest')
        break
      case HttpStatusCode.Unauthorized:
        removeAuthToken()
        idb.clear()
        showNotify = false
        redirectToLogin()
        break
      case HttpStatusCode.NotFound:
        message = getI18n('ErrorMessage.NotFound')
        break
      case HttpStatusCode.InternalServerError:
        message = getI18n('ErrorMessage.InternalServerError')
        break
      case HttpStatusCode.ServerMaintain:
        window.location.href = getEnvConfig('linkPageMaintain')
        break
      default:
        showNotify = false
        typeof handleCustomError === 'function' && handleCustomError(error)
    }
  }

  showNotify &&
    message &&
    notify.showError({
      message: initMessage || message
    })
}

/**
 * @function Lấy domain hiện tại của app Ôn tập
 * @returns
 * @author PHNam
 */
export function getCurrentDomain (): string {
  return `${window.location.origin}${window.__env.base_router}`
}

/**
* @function
* @description Loại bỏ các phần độc hại của text
* @param {*} val
* @returns
* @author PHNam
*/
export function removeDangerousString (val: string) {
  const p = document.createElement('p')

  p.innerHTML = val

  return p.textContent || ''
}

export function redirectToLogin () {
  const fullPath = window.location.href.replace(getCurrentDomain(), '')
  const queries = []
  const encodePath = encodeURIComponent(fullPath)

  const queryParam: Record<string, any> = {
    type: fullPath.startsWith('student') || fullPath.startsWith('/student') ? 'student' : 'teacher',
    redirect: encodePath
  }

  for (const param in queryParam) {
    queries.push(`${param}=${queryParam[param]}`)
  }

  redirectToApp(App.Account, `login?${queries.join('&')}`)
}

/**
 * @description Show help
 * @param helpID
 * @author PHNam
 * @creted 21/07/2021
 */
export function showHelp (helpID: string | undefined) {
  const HELP_BASE_URL = 'https://emishelp.misa.vn/kb/'
  window.open(`${HELP_BASE_URL}${helpID || ''}`)
}

/**
   * @description Thực hiện ẩn tính năng kiểm tra theo đơn vị đã add trong config
   * @author NTQuy
   * @returns
   */
export function isShowExamFeature (redirect: Boolean | null | undefined) {
  const organization = getContextData(ContextDataKey.OrganizationID)
  let isInclude = true

  if (window.__env.organizationExamShows?.length === 0) {
    return isInclude
  }

  isInclude = window.__env.organizationExamShows.includes(organization)

  if (!isInclude && redirect) {
    window.location.href = getCurrentDomain()
  }

  return isInclude
}

class CommonFn {
  handleNetworkError: (
    error: any,
    handleCustomError?: Function | undefined
  ) => void

  focusFirstControl: (container: HTMLElement) => void
  redirectToApp: (app: string, path?: string) => void
  subjectImageNames: Array<string> | null

  constructor () {
    this.focusFirstControl = focusFirstControl
    this.handleNetworkError = handleNetworkError
    this.redirectToApp = redirectToApp
    this.subjectImageNames = null
  }

  /**
   * Xử lý lưu lại thông tin phục vụ tracking
   * @param {String} msg Thông tin cần log
   */
  infoLogging (msg: string) {
    if (process.env.NODE_ENV === 'development' && msg) {
      console.debug(msg)
    }
  }

  /**
   * Xử lý lưu lại thông tin phục vụ tracking
   * @param {String} msg Thông tin cần log
   */
  warnLogging (msg: string) {
    if (process.env.NODE_ENV === 'development' && msg) {
      console.warn(msg)
    }
  }

  /**
   * Xử lý lưu lại thông tin phục vụ tracking
   * @param {string | Exception} msg Thông tin cần lưu
   */
  errorLogging (msg: string) {
    if (process.env.NODE_ENV === 'development' && msg) {
      console.error(msg)
    }
  }

  /**
   * Gộp url
   * @returns
   */
  combineUrl (...args: any) {
    if (args && args.length > 0) {
      const arr = []

      for (let i = 0; i < arguments.length; i++) {
        const s = args[i]
        const reg = new RegExp('^/*(.*?)/*$')
        arr.push(s.replace(reg, '$1'))
      }
      return arr.join('/')
    }
  }

  /**
   * Show mask
   */
  mask () {
    const appLoading = document.getElementById('loading-bg')
    const bgLoading = document.getElementById('loading-filler')
    if (appLoading) {
      appLoading.style.display = 'none'
    }
    if (bgLoading) {
      bgLoading.style.display = 'none'
    }
  }

  /**
   * Hide mask
   */
  unmask () {
    const appLoading = document.getElementById('loading-bg')
    const bgLoading = document.getElementById('loading-filler')
    if (appLoading) {
      appLoading.style.display = 'none'
    }
    if (bgLoading) {
      bgLoading.style.display = 'none'
    }
  }

  /**
   * @description Kiểm tra có thay đổi so với dữ liệu ban đầu
   * @param {*} oldVal
   * @param {*} newVal
   * @param {*} ignoreProps Các thuộc tính bỏ qua
   * @author PHNam
   */
  checkChange (oldVal: object, newVal: object, ignoreProps: any[]) {
    let isChanged = false
    if (typeof newVal === 'object' && typeof oldVal === 'object') {
      for (const field in newVal) {
        if (ignoreProps && ignoreProps.includes(field)) {
          continue
        }
        // @ts-ignore
        if (typeof newVal[field] === 'object' && typeof oldVal[field] === 'object') {
          // @ts-ignore
          this.checkChange(newVal[field], oldVal[field])
        } else {
        // @ts-ignore
          if (newVal[field] !== oldVal[field]) {
            isChanged = true
            break
          }
        }
      }
    }

    return isChanged
  }

  /**
   * @description Lấy ra DOM của container
   * @param {*} container
   * @author PHNam
   */
  getContainerElement (container: any) {
    return container && container._isVue ? container.$el : container
  }

  /**
   * @description Lấy ra instance validate
   * @param {*} container
   * @author PHNam
   */
  getValidateInstance (container: any) {
    const me = this
    const validateClass = '.ms-form'
    const containerEl = me.getContainerElement(container)
    const validateEl = containerEl
      ? containerEl.classList.contains(validateClass)
        ? containerEl
        : containerEl.querySelector(validateClass)
      : null

    return validateEl ? validateEl.__vue__ : null
  }

  /**
   * @description Hiển thị mask loading
   * @param {*} container
   * @author PHNam
   */
  showMask (container: any, message: string, customClass: string) {
    const me = this
    const target = me.getContainerElement(container)

    message = message ?? i18n?.global?.t('Controls.Loading')
    if (target && !target.__loadingInstance) {
      target.__loadingInstance = ElLoading.service({
        target: container,
        fullscreen: false,
        lock: true,
        text: message,
        customClass: customClass
      })
    }
  }

  /**
   * @description Ẩn mask loading
   * @param {*} container
   * @author PHNam
   */
  hideMask (container: any) {
    const loadingInstance = container?.__loadingInstance

    if (loadingInstance) {
      loadingInstance && loadingInstance.close()
      container.__loadingInstance = null
    }
  }

  /**
   * @description Trả về instance của Component
   * @param {*} component
   * @param {*} props
   * @author PHNam
   */
  getComponentInstance (component: any, props?: Record<string, unknown>) {
    const instance = createApp(component?.default || component, props)

    setupPlugins(instance, window.__appName)

    return instance
  }

  /**
   * Lấy enum source
   * @param {*} enumName
   * @param {*} includeKeys
   * @returns
   * HNTHANG 30.03.2021
   */
  getEnumData (enumName: string, includeKeys: string[]) {
    const ret: any[] = []

    // @ts-ignore
    Object.keys(enumeration[enumName]).forEach((key) => {
      let item = null

      if (includeKeys && includeKeys.length > 0) {
        if (includeKeys.includes(key)) {
          item = {
            // @ts-ignore
            value: enumeration[enumName][key],
            display: getI18n(`EnumResource.${enumName}.${key}`)
          }
          ret.push(item)
        }
      } else {
        item = {
          // @ts-ignore
          value: enumeration[enumName][key],
          display: getI18n(`EnumResource.${enumName}.${key}`)
        }

        ret.push(item)
      }
    })

    return ret
  }

  /**
   * @description Lấy maskObject theo type để truyền vào v-mask
   * @param {*} type type mask của MaskTypes trong constant
   * @returns
   * @author PHNam
   */
  getMaskInput (type: number) {
    const MASK = 'mask'
    let maskVal = ''
    // const TOKENS = 'tokens'
    const maskObj = {}

    switch (type) {
      case MaskTypes.DATE:
        maskVal = '##/##/####'
        break
      case MaskTypes.INTEGER:
        maskVal = '###.###.###.###.###'
        break
      case MaskTypes.FLOAT:
        maskVal = '###.###.###.###.###,##'
        break
      default:
        maskVal = ''
    }

    Object.defineProperty(maskObj, MASK, {
      value: maskVal,
      writable: true,
      enumerable: true,
      configurable: true
    })

    return maskObj
  }

  /**
   * Chỉ thêm giá trị khi cookie ko có giá trị
   * Proxy Protection
   * @param {*} cname
   * @param {*} cvalue
   * @param {*} exdays
   */
  setCookieProxy (cname: string, cvalue: any, exdays: number) {
    const me = this
    const value = me.getCookie(cname)

    if (value === '') {
      me.setCookie(cname, cvalue, exdays)
    }
  }

  setCookie (cname: string, cvalue: any, exdays: number) {
    return setCookie(cname, cvalue, exdays)
  }

  getCookie (cname: string) {
    return getCookie(cname)
  }

  /**
   * Lấy thông tin thiết bị dạng json stringify
   * @returns
   */
  getDeviceInfoStringify () {
    const data = {
      DeviceId: this.getCookie(CookieType.DeviceId),
      UserAgent: navigator.userAgent
    }

    return JSON.stringify(data)
  }

  /**
   * @description Copy text to clipboard
   * @author NTQuy
   * @param {*} text text muốn copy
   * @param {String} message Message muốn thông báo
   */
  copyToClipboard (text: string, message?: string) {
    const el = document.createElement('textarea')

    el.value = text.toString()
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)

    if (message) {
      notify.showSuccess({
        message: message
      })
    }
  }

  // @ts-ignore
  // @ts-nocheck
  buildPreviewFileUrl ({ fileId, storageType, ownerId, clientFileName, downloadFileName = '', isPreview, width, isTemp = false, modifiedDate = null }) {
    const origin =
      process.env.NODE_ENV === 'development' ? '' : window.location.origin
    const baseUrl = origin + getApiUrl(apis.FileAPI)
    const resource = 'v1/files/download'
    if (isTemp) {
      storageType = enumeration.StorageType.Temp
    }
    let queryParam = `FileID=${fileId}&StorageType=${storageType}&OwnerID=${ownerId}&IsPreview=${isPreview}`
    if (clientFileName != null) {
      queryParam += `&ClientFileName=${clientFileName}`
    }
    if (width != null) {
      queryParam += `&Width=${width}`
    }
    if (downloadFileName) {
      queryParam += `&DownloadFileName=${downloadFileName}`
    }
    if (modifiedDate) {
      // @ts-ignore
      // @ts-nocheck
      const timeStamp = new Date(modifiedDate).getTime()
      queryParam += `&ModifiedDate=${timeStamp}`
    }
    return `${baseUrl}/${resource}?${queryParam}`
  }

  getKeyColumns (dataColumns: Array<any>, Keys: Array<any>) {
    for (let i = 0; i < Keys.length; i++) {
      dataColumns[i].Key = Keys[i].value
    }
  }

  /**
   * @description hàm xử lý tải về file template excel
   * @param type enum có trong bộ enum. template cấu hình dưới DB collection ImportTemplate
   * @author BNDung
   */
  downloadFile (url: String) {
    const iframeId = 'downloadIframe'
    let iframe = document.getElementById(iframeId)
    if (!iframe) {
      iframe = document.createElement('iframe')
      iframe.id = iframeId
    }

    iframe.setAttribute('src', `${url}`)
    iframe.setAttribute('class', 'hidden')

    document.body.appendChild(iframe)
  }

  /**
   * @description hàm xử lý tải về file excel
   * @param cacheKey key để download file
   * @author BNDung
   */
  downloadFileExcel (cacheKey: String) {
    const iframeId = 'downloadIframe'
    let iframe = document.getElementById(iframeId)
    if (!iframe) {
      iframe = document.createElement('iframe')
      iframe.id = iframeId
    }
    const exportAPI = ExportAPI

    const filePath = `${exportAPI.baseURL}/${exportAPI.controller}/download?key=${cacheKey}`
    iframe.setAttribute('src', `${filePath}`)
    iframe.setAttribute('class', 'hidden')

    document.body.appendChild(iframe)
  }

  // /**
  //  * lấy đường dẫn URL Full VD: localhost:8080/v1/student/
  //  */
  // getApiUrlFull (module: String) {
  //   const result = module.replace(/([A-Z])/g, '-$1')
  //   const finalResult = (result.charAt(0).toLowerCase() + result.slice(1)).toLowerCase()
  //   return `${getApiUrl(apis[module])}/v1/${finalResult}/`
  // }

  /** */
  async onReceivePushMessage () {
    const socket = window.io(getEnvConfig('socketIOPath'), {
      transports: ['websocket']
    }).connect()
    socket.emit(SocketEvent.JoinRoom, {
      // @ts-ignore
      UserID: window.__contextData.UserID
    })
    // @ts-ignore
    socket.on(SocketEvent.NewNotify, function (socketData: SocketData) {
      emit(socketData.EventName, socketData.Data)
    })
  }

  /**
   * @description kiểm tra độ hợp lệ của chuỗi a-z & 0-9
   * @param value giá trị chuỗi truyền vào
   * @returns true/false
   */
  checkValidString (value: string) {
    const pattern = /^[a-zA-Z0-9]*$/
    const matches = pattern.exec(value)
    const matchStatus = Boolean(matches)
    return matchStatus
  }

  /**
   * @author BNDung
   * @description loại bỏ nhiều dấu cách thành 1 dấu cách
   * @param value giá trị chuỗi truyền vào
   * @returns
   */
  formatSpace (value: String) {
    return value?.toString().trim().toLowerCase().replace(/\s+/g, '')
  }

  /**
   * chuyển đổi từ tiếng việt sang tiếng unicode
   * @author BNDung
   */
  nonAccentVietnamese (str: String) {
    str = str.toLowerCase()
    //     str = str.replace(/\u00E0|\u00E1|\u1EA1|\u1EA3|\u00E3|\u00E2|\u1EA7|\u1EA5|\u1EAD|\u1EA9|\u1EAB|\u0103|\u1EB1|\u1EAF|\u1EB7|\u1EB3|\u1EB5/g, "a");
    //     str = str.replace(/\u00E8|\u00E9|\u1EB9|\u1EBB|\u1EBD|\u00EA|\u1EC1|\u1EBF|\u1EC7|\u1EC3|\u1EC5/g, "e");
    //     str = str.replace(/\u00EC|\u00ED|\u1ECB|\u1EC9|\u0129/g, "i");
    //     str = str.replace(/\u00F2|\u00F3|\u1ECD|\u1ECF|\u00F5|\u00F4|\u1ED3|\u1ED1|\u1ED9|\u1ED5|\u1ED7|\u01A1|\u1EDD|\u1EDB|\u1EE3|\u1EDF|\u1EE1/g, "o");
    //     str = str.replace(/\u00F9|\u00FA|\u1EE5|\u1EE7|\u0169|\u01B0|\u1EEB|\u1EE9|\u1EF1|\u1EED|\u1EEF/g, "u");
    //     str = str.replace(/\u1EF3|\u00FD|\u1EF5|\u1EF7|\u1EF9/g, "y");
    //     str = str.replace(/\u0111/g, "d");
    str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a')
    str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e')
    str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i')
    str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ| |ờ|ớ|ợ|ở|ỡ/g, 'o')
    str = str.replace(/ù|ú|ụ|ủ|ũ|ừ|ứ|ự|ử|ữ/g, 'u')
    str = str.replace(/ư/g, 'w')
    str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y')
    str = str.replace(/đ/g, 'd')
    // Some system encode vietnamese combining accent as individual utf-8 characters
    str = str.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, '') // Huyền sắc hỏi ngã nặng
    str = str.replace(/\u02C6|\u0306|\u031B/g, '') // Â, Ê, Ă, Ơ, Ư
    return str
  }

  /**
   * @author BNDung
   * @description so sánh hai chuỗi
   * @param parent giá trị chuỗi cha
   * @param child giá trị chuỗi con cần kiểm tra
   * @returns
   */
  stringComparison (first: String, second: String) {
    const firstFormat = first.toString().trim().toLowerCase().replace(/\s+/g, '')
    const secondFormat = second.toString().trim().toLowerCase().replace(/\s+/g, '')
    let result = false

    if (firstFormat === secondFormat) { result = true }
    return result
  }

  /**
   * @author BNDung
   * @param {*} object đối tượng teacher hoặc student truyền vào để lấy file ảnh
   * @returns lấy ra file ảnh dưới DB nếu không có thì load default image
   */
  // getImageDB (Object: any) {
  //   let imageName = ''
  //   if (Object?.object?.Subjects?.length === 1) {
  //     imageName = `${Object?.object?.Subjects[0]?.SubjectCode.toLowerCase()}.png`
  //   } else {
  //     imageName = 'default.png'
  //   }
  //   return require(`@/assets/images/png/class-room/${imageName}`)
  // }

  /**
   * @author PVThang
   * @param image ID hoặc tên ảnh
   * @param studentID ID của học sinh (cần dùng trong trường hợp image là uuid)
   * @param width chiều rộng ảnh của học sinh (cần dùng trong trường hợp image là uuid)
   * @returns ảnh của học sinh
   */
  getStudentImage (image: String, studentID: String | null, width: Number | null) {
    const me = this

    if (!image || image === undefined) {
      return require('@/assets/images/svg/avatar/student/default/default.svg')
    } else {
      if (!isUuid(image)) {
        return require('@/assets/images/svg/avatar/student/' + image + '.svg')
      } else {
        // return getContextData(ContextDataKey.Image)
        return me.buildPreviewFileUrl({
          fileId: image,
          storageType: enumeration.StorageType?.StudentAvatar,
          ownerId: studentID,
          clientFileName: '',
          isPreview: true,
          width: width || ''
        })
      }
    }
  }

  /**
   * @author PBac
   * @param frame tên khung ảnh đại diện của học sinh
   * @returns khung ảnh đại diện
   */
  getStudentAvatarFrame (frame: String) {
    return require('@/assets/icons/svg/user_info/' + frame + '_frame.svg')
  }

  /**
   * @author PVThang
   * @returns Danh sách tên ảnh theo môn
   */
  getSubjectImageNames () : Array<string> {
    const imageNameDefault = 'default'
    const context = require.context(
      '@/assets/images/png/subject',
      false,
      /[A-Za-z0-9-_,\s]+\.(png)$/i
    )
    let matched = null
    const fileNames = context.keys().map((key) => {
      matched = key?.match(/([A-Za-z0-9-_]+)\./i)
      if (matched && matched.length > 1) {
        return matched[1] // Tên file
      } else {
        return imageNameDefault
      }
    })

    return fileNames
  }

  /**
   * @description hàm lấy mã màu từ subjectCode
   * @author BNDung
   * @param subjectCode
   */
  getSubjectBackgroundColor (subjectCode: string) {
    const me = this
    const enumSubject = enumeration.SubjectCode
    let result = ''

    switch (subjectCode) {
      case enumSubject.DIALI:
      case enumSubject.KHTN:
      case enumSubject.LICHSU:
      case enumSubject.SINHHOC:
      case enumSubject.TIENGANH:
      case enumSubject.DEFAULT:
        result = 'bg-semantic-blue'
        break

      case enumSubject.NGUVAN:
      case enumSubject.TIENGVIET:
      case enumSubject.GDCD:
        result = 'bg-semantic-green'
        break

      case enumSubject.HOAHOC:
      case enumSubject.TIENGNGA:
      case enumSubject.TIENGNHAT:
      case enumSubject.TIENGTQ:
        result = 'bg-semantic-pink'
        break

      case enumSubject.TOAN:
        result = 'bg-accent-200'
        break

      case enumSubject.VATLI:
        result = 'bg-semantic-yellow'
        break

      default:
        result = 'bg-semantic-blue'
        break
    }
    return result
  }

  /**
   * @author PVThang
   * @param imageName Tên ảnh theo môn
   * @returns ảnh môn học
   */
  getSubjectImage (imageName: string | null | undefined) : string {
    const me = this
    const imageNameDefault = 'default'
    const imageNameLowerCase = imageName?.toLowerCase() || imageNameDefault
    if (!me.subjectImageNames) {
      me.subjectImageNames = me.getSubjectImageNames()
    }

    const imageNameResult = me.subjectImageNames?.includes(imageNameLowerCase) ? imageNameLowerCase : imageNameDefault
    return require(`@/assets/images/png/subject/${imageNameResult}.png`)
  }

  /**
   * @author PVThang
   * @description Lấy tên ảnh ngẫu nhiên theo subjectCode
   * @param {string | undefined | null} subjectCode mã môn học
   */
  randomSubjectImage (subjectCode: string | undefined | null) : string {
    const me = this
    const imageNameDefault = 'default'
    const subjectCodeLowerCase = subjectCode?.toLowerCase() || imageNameDefault
    if (!me.subjectImageNames) {
      me.subjectImageNames = me.getSubjectImageNames()
    }
    const fileNamesOfCurrentSubject = me.subjectImageNames?.filter(fileName => {
      return fileName?.startsWith(subjectCodeLowerCase)
    }) || []
    const indexFileNameRandom = Math.round(
      Math.random() * (fileNamesOfCurrentSubject.length - 1)
    )

    return fileNamesOfCurrentSubject[indexFileNameRandom] || imageNameDefault
  }

  /**
   *
   */
  queueTypeSetMathJax () {
    // @ts-ignore
    if (window.__contextData.timeoutVar) {
      // @ts-ignore
      clearTimeout(window.__contextData.timeoutVar)
    }
    // @ts-ignore
    const outputMathJax = window.MathJax.startup.output

    if (outputMathJax && typeof (outputMathJax.clearFontCache) === 'function') {
      outputMathJax.clearFontCache()
    }
    if (outputMathJax && typeof (outputMathJax.clearCache) === 'function') {
      outputMathJax.clearCache()
    }
    // @ts-ignore
    window.__contextData.timeoutVar = setTimeout(() => {
      // @ts-ignore
      window.MathJax.typeset()
    }, 300)
  }

  /**
   * @author PVThang
   * @param imageFile file ảnh
   * @param width độ rộng
   * @returns src ảnh
   */
  getExerciseOrClassImage (imageFile: File | null | undefined, width: number | undefined) {
    const me = this
    if (imageFile && isUuid(imageFile.FileID) && emptyUuid !== imageFile.FileID) {
      return me.buildPreviewFileUrl({
        fileId: imageFile?.FileID,
        storageType: imageFile?.StorageType,
        ownerId: imageFile.OwnerID,
        clientFileName: imageFile?.FileName,
        isTemp: imageFile?.IsTemp,
        isPreview: true,
        width: width || ''
      })
    } else {
      return me.getSubjectImage(imageFile?.FileName)
    }
  }

  /**
   * @description Hàm kiểm tra email có nằm trong config hay không
   * @author NTQuy
   * @returns
   */
  isPartnerInsideConfig () {
    const email = getContextData(ContextDataKey.Email)
    const phoneNumber = getContextData(ContextDataKey.PhoneNumber)
    let isInclude = true

    isInclude = window.__env.partnerEmails.includes(email) ||
    window.__env.partnerPhoneNumbers.includes(phoneNumber)

    return isInclude
  }

  /**
   * @description Lấy random số ngẫu nhiên trong giới hạn
   * @author NTQuy
   * @param min
   * @param max
   * @returns
   */
  getRandomIntNumber (min: number, max: number) {
    min = Math.ceil(min)
    max = Math.floor(max)
    return Math.floor(Math.random() * (max - min + 1) + min) // The maximum is inclusive and the minimum is inclusive
  }

  /**
   * @description Lấy random ảnh đại diện lớp
   * @author NTQuy
   * @returns
   */
  getClassroomCoverImage (image: string, imageWidth: number, ownerID: string) {
    const min = 1
    const max = CoverImageClassroomCount
    const number = this.getRandomIntNumber(min, max)
    const imageDefault = `classroom${number}.jpg`
    let fileName = null

    image ? fileName = image : fileName = imageDefault

    if (isUuid(image)) {
      return this.buildPreviewFileUrl({
        fileId: image,
        storageType: enumeration.StorageType?.TeacherClass,
        ownerId: ownerID,
        clientFileName: '',
        isTemp: false,
        isPreview: true,
        width: imageWidth
      })
    }

    let data = null

    try {
      data = {
        src: require(`@/assets/images/png/class-room/${fileName}`),
        fileName: fileName
      }
    } catch (error) {
      data = {
        src: require(`@/assets/images/png/class-room/${imageDefault}`),
        fileName: imageDefault
      }
    }

    return data
  }

  /**
   * @description Encode các ký tự html
   * @param value
   * @returns
   * @author PHNam
   */
  encodeHtml (value: string) {
    return encode(value)
  }

  /**
   * @description merge text subject
   * @author VDDung1
   */
  mergeSubjectText (arr: Array<any>) {
    const slash = ', '
    let subjectsName: string[] = []
    let result = ''
    // lấy dữ liệu từ mảng sau đó push vào mảng text
    subjectsName = arr?.map(element => element.SubjectName)
    result = subjectsName?.join(slash)
    return result
  }

  /**
   * @description merge text class
   * @author VDDung1
   */
  mergeClassText (arr: Array<any>) {
    const slash = ', '
    let className: string[] = []
    let result = ''
    // lấy dữ liệu từ mảng sau đó push vào mảng text
    className = arr?.map(element => element.ClassName)
    result = className?.join(slash)
    return result
  }

  /**
   * @description Lấy random background
   * @author VDDung1
   * @returns
   */
  randomBackgroundImage (urlImage: string, countImage: number, formatImage: string) {
    const min = 1
    const max = countImage
    const number = this.getRandomIntNumber(min, max)
    return require(`@/assets/${urlImage}_${number}.${formatImage}`)
  }

  /**
   * @description gen class chia layout cho đáp án câu hỏi theo độ dài đáp án
   * @author NTQuy
   * @param answers list answer
   * @param isGrid dùng grid hay flex để chia layout
   * @returns
   */
  generateLayoutByTextLength (answers: Array<any>, isGrid: Boolean) {
    const lengthFormat = {
      ONE_COL: 52, // Độ dài đáp án break 1 cột
      TWO_COL: 24 // Độ dài đáp án break 2 cột
    }
    let customClass = 'four-col'

    if (isGrid) customClass = 'grid-cols-4'

    answers?.every(answer => {
      const contentLength = answer?.Content?.replace(/(<([^>]+)>)/gi, '')?.length

      if (contentLength > lengthFormat.ONE_COL) {
        isGrid ? customClass = 'grid-cols-1' : customClass = 'one-col'

        return false
      } else if (contentLength > lengthFormat.TWO_COL) {
        isGrid ? customClass = 'grid-cols-2' : customClass = 'two-col'
      }

      return true
    })

    return customClass
  }

  /**
   * Kiểm tra quyền của user đăng nhập
   * @param roleUser
   * @returns
   * true là có quyền, false là không có quyền
   */
  checkRoleUser (roleUser: Number) {
    let checkRole = false

    const listRoleContext = getContextData(ContextDataKey.RoleList)
    if (listRoleContext !== '' && listRoleContext !== undefined) {
      const listRoles = JSON.parse(listRoleContext)
      const role = listRoles?.find((item: RoleUser) => item.DictionaryKey === roleUser)
      checkRole = role !== undefined
    }
    return checkRole
  }

  /**
    * Check quyền tổ chức hội đồng và show cảnh báo
    * @returns
    */
  allowActionByRoleOrganingCommittee () {
    const me = this
    const checkRole = me.checkRoleUser(enumeration.RoleDictionaryKey.OrganingCommittee)
    if (!checkRole) {
      notify.showWarning({
        message: getI18n('ErrorMessage.UnallowRole'),
        dangerouslyUseHTMLString: true
      })
    }
    return checkRole
  }

  /**
   * Di chuyển element đến vị trí mới
   * @author THNhat
   * @param elementMove element cần di chuyển, element phải Position absolute hoặc relative
   * @param xTargetPos left của vị trí cần tới
   * @param yTargetPos top của vị trí cần tới
   * @param speed Tốc độ mong muốn >= 0 (defalut = 1)
   * @param DirectionCurve Hướng cong (true, false tùy vị trí thay vào để thử, default random)

   */

  moveElement (elementMove: any, xTargetPos : number, yTargetPos: number, speed: number = 1, DirectionCurve: boolean = Math.random() < 0.5) {
    let multipleX = 1
    let multipleY = 1
    let acceleration = 1
    const beginLeft = elementMove.style.left
    const beginTop = elementMove.style.top
    speed = Math.max(speed, 0)
    speed *= 80
    const deltaT = 0.01
    const updatePos = setInterval(() => {
      const multiple = Math.random() + 1
      acceleration += deltaT
      if (DirectionCurve) multipleX = multiple
      else multipleY = multiple
      const xCurrentPos = elementMove.getBoundingClientRect().x
      const yCurrentPos = elementMove.getBoundingClientRect().y
      const xDistance = xCurrentPos - xTargetPos
      const yDistance = yCurrentPos - yTargetPos
      const distance = Math.sqrt(xDistance * xDistance + yDistance * yDistance)
      elementMove.style.left = xCurrentPos - (xDistance / distance) * deltaT * multipleX * acceleration * speed + 'px'
      elementMove.style.top = yCurrentPos - (yDistance / distance) * deltaT * multipleY * acceleration * speed + 'px'
      if (distance < 10) {
        clearInterval(updatePos)
        elementMove.style.left = beginLeft
        elementMove.style.top = beginTop
      }
    }, deltaT)
  }

  /**
   * Animation số chạy đến số target
   * @author THNhat
   * @param element element thực hiện animation
   * @param target giá trị mục tiêu
   * @param time Thời gian tăng (default = 5s)

   */
  counterAnimation (element: any, target: number, time: number = 5) {
    // Lower inc to slow and higher to slow
    time *= 50
    const inc = Math.max(1, Math.round(target / time))
    let count = +element.innerHTML
    // Call function every 10 ms
    const updateCount = setInterval(() => {
    // Check if target is reached
      if (count < target) {
        // Add inc to count and output in counter
        count += inc
        element.innerHTML = formatNumber(count, 0, 3, '.', '')
      } else {
        element.innerHTML = formatNumber(target, 0, 3, '.', '')
        clearInterval(updateCount)
      }
    }, 10)
  }

  /**
   * Animation scale element trong khoảng thời gian mong muốn
   * @author THNhat
   * @param element element thực hiện animation
   * @param scaleX
   * @param scaleY
   * @param time (default = 1)
   */
  scaleInTime (element : any, scaleX : number, scaleY: number, time:number = 1) {
    let currX = 1
    let currY = 1
    const diffX = currX - scaleX
    const diffY = currY - scaleY
    const decX = diffX / time / 100
    const decY = diffY / time / 100
    const updateScale = setInterval(() => {
      if (currX - scaleX >= 2 * decX || currY - scaleY >= 2 * decY) {
        currX = currX === scaleX ? currX : currX - decX
        currY = currY === scaleY ? currY : currY - decY
        element.style.transform = `scale(${currX},${currY})`
      } else {
        clearInterval(updateScale)
      }
    }, 10)
  }

  /**
  * Animation move và thu nhỏ về 0 cùng 1 lúc
   * @author THNhat
   * @param elementMove element cần di chuyển, element phải Position absolute hoặc relative
   * @param xTargetPos left của vị trí cần tới
   * @param yTargetPos top của vị trí cần tới
   * @param speed Tốc độ mong muốn >= 0 (defalut = 1)
   * @param DirectionCurve Hướng cong (true, false tùy vị trí thay vào để thử, default random)
  */
  moveAndScale (elementMove: any, xTargetPos : number, yTargetPos: number, speed: number = 1, DirectionCurve: boolean = Math.random() < 0.5) {
    elementMove.style.display = 'block'
    let multipleX = 1
    let multipleY = 1
    let acceleration = 1
    const beginFontSize = parseInt(elementMove.style.fontSize)
    if (!speed) speed = 1
    speed = Math.max(speed, 0)
    speed *= 80
    const deltaT = 0.01
    const diffFont = beginFontSize
    const dec = diffFont / 3 / 100
    let currDec = 0

    const updatePos = setInterval(() => {
      const multiple = Math.random() + 1
      acceleration += deltaT
      if (DirectionCurve) multipleX = multiple
      else multipleY = multiple
      const xCurrentPos = elementMove.getBoundingClientRect().x
      const yCurrentPos = elementMove.getBoundingClientRect().y
      const xDistance = xCurrentPos - xTargetPos
      const yDistance = yCurrentPos - yTargetPos
      const distance = Math.sqrt(xDistance * xDistance + yDistance * yDistance)

      elementMove.style.left = xCurrentPos - (xDistance / distance) * deltaT * multipleX * acceleration * speed + 'px'
      elementMove.style.top = yCurrentPos - (yDistance / distance) * deltaT * multipleY * acceleration * speed + 'px'

      currDec += dec
      elementMove.style.fontSize = beginFontSize - currDec + 'px'
      if (distance < 10) {
        clearInterval(updatePos)
        elementMove.style.display = 'none'
      }
    }, deltaT)
  }

  /**
  * So sánh 2 đối tượng để sắp xếp từ bé đến lớn
   * @author THNhat
   * @param object1 đối tượng 1
   * @param object2 đối tượng 2
   * @param key key để so sánh (2 đối tượng phải có chung key này)
  */
  compareObjects (object1:any, object2:any, key:any) {
    const obj1 = object1[key]
    const obj2 = object2[key]

    if (obj1 < obj2) {
      return -1
    }
    if (obj1 > obj2) {
      return 1
    }
    return 0
  }

  /**
  * So sánh 2 đối tượng để sắp xếp từ lớn đến bé
   * @author THNhat
   * @param object1 đối tượng 1
   * @param object2 đối tượng 2
   * @param key key để so sánh (2 đối tượng phải có chung key này)
  */
  compareDecObjects (object1:any, object2:any, key:any) {
    const obj1 = object1[key]
    const obj2 = object2[key]

    if (obj1 > obj2) {
      return -1
    }
    if (obj1 < obj2) {
      return 1
    }
    return 0
  }

  /**
   * Convert text có dạng {#number} về '...'
   * @param text
   * @returns
   */
  convertFillBlankText (text:string) {
    if (text) {
      return text.replace(/{#\d}/g, '...')
    }
    return text
  }

  /**
   * Convert tên background class sang border class
   * @param text
   * @returns
   */
  convertBackgroundColorToBorderColor (backgroundColor:string) {
    return backgroundColor.replace(/bg/g, 'border-color')
  }

  /**
   * Khoảng cách câu hỏi
   * @param text
   * @returns
   */
  // convertQuestion (text:string) {
  //   if (text) {
  //     return text.replace(/\\,/g, '').replaceAll('\\[', '').replaceAll('\\times', 'x')
  //   }
  //   return text
  // }

  /**
   * Convert text có tag và kí tự đặc biệt về dạng plain text
   * @param text
   * @returns
   */
  convertToPlainText (text : string) {
    if (text) {
      return text.replace(new RegExp('<[^>]*>', 'g'), '')
        .replaceAll('&lt;', '<')
        .replaceAll('&gt;', '>')
        .replaceAll('&amp;', '&')
        .replaceAll('&nbsp;', ' ')
        .replaceAll('&quot;', '"')
        .replaceAll('&#039;', "'")
        .replaceAll(String.fromCharCode(8217), "'") // Đổi dấu "'"
        .replaceAll('\n', '') // Xử lý dấu Enter
    }
    return ''
  }

  /**
   * Convert text sang dạng html
   * @param text
   * @returns
   * @author lhlong
   */
  convertTextToHtml (text : string) {
    if (text) {
      return text.replaceAll('\s', '&nbsp;')
        .replaceAll('\t', '&emsp;')
        .replaceAll('\n', '<br>') // Xử lý dấu Enter
    }
    return ''
  }

  /**
   * Trả về năm học
   * @returns
   */
  getSchoolYearIndexDB () {
    // const key = idbKeys.SchoolYear
    // return await idb.get(key, [])
    return localStorage.get(localStorageKeys.SCHOOL_YEAR)
  }

  /**
   * Convert text thành editor view cho học sinh
   * @param text
   * @author THNhat
   * @returns
   */
  convertEditorPlainStudent (text: string) {
    const regBlankBox = /<span class="blank-box"([^>]{0,})>/g
    const blankBoxSymbol = '//((blank-box))//'
    const listBlankBox = text.match(regBlankBox) || []
    text = text.replace(regBlankBox, blankBoxSymbol)
    text = text.replace(new RegExp('<[^>]*>', 'g'), '')
    for (let i = 0; i < listBlankBox?.length; i++) {
      const realTag = listBlankBox[i] + '</span>'
      const container = document.createElement('div')
      container.innerHTML = realTag
      const blankBoxElement = container.querySelector('.blank-box')
      blankBoxElement?.removeAttribute('data-correct')
      blankBoxElement?.setAttribute('is-teacher', 'false')
      const replaceTag = container.innerHTML
      text = text.replace(blankBoxSymbol, replaceTag)
    }
    text = text.trim()
    return text
  }

  /**
   * Convert text thành editor view cho học sinh
   * @param text
   * @author THNhat
   * @returns
   */
  convertEditorStudent (text: string) {
    const regBlankBox = /<span class="blank-box"([^>]{0,})>/g
    const blankBoxSymbol = '//((blank-box))//'
    const listBlankBox = text.match(regBlankBox) || []
    text = text.replace(regBlankBox, blankBoxSymbol)
    text = this.convertToPlainText(text)
    for (let i = 0; i < listBlankBox?.length; i++) {
      const realTag = listBlankBox[i] + '</span>'
      const container = document.createElement('div')
      container.innerHTML = realTag
      const blankBoxElement = container.querySelector('.blank-box')
      blankBoxElement?.removeAttribute('data-correct')
      blankBoxElement?.setAttribute('is-teacher', 'false')
      const replaceTag = container.innerHTML
      text = text.replace(blankBoxSymbol, replaceTag)
    }
    text = text.trim()
    return text
  }

  /**
   * Convert text thành editor view cho giáo viên
   * @param text
   * @author THNhat
   * @returns
   */
  convertEditorPlainTeacher (text: string) {
    const regBlankBox = /<span class="blank-box"([^>]{0,})>/g
    const blankBoxSymbol = '//((blank-box))//'
    const listBlankBox = text.match(regBlankBox) || []
    text = text.replace(regBlankBox, blankBoxSymbol)
    text = this.convertToPlainText(text)
    for (let i = 0; i < listBlankBox?.length; i++) {
      text = text.replace(blankBoxSymbol, listBlankBox[i] + '</span>')
    }
    text = text.trim()
    return text
  }

  /**
   * get đáp án của các câu hỏi blank box
   * @param text
   * @author THNhat
   * @returns
   */
  getBlankBoxAnswer (text: string) {
    const regBlankBox = /data-correct="([^>]{0,})"/g
    const blankBoxSymbol = '//((blank-box))//'
    const answer = []
    const listDataCorrect = text.match(regBlankBox) || []
    for (let i = 0; i < listDataCorrect?.length; i++) {
      listDataCorrect[i] = listDataCorrect[i].replaceAll('data-correct=', '')
        .replaceAll('"', '')
        .replaceAll('[', '')
        .replaceAll(']', '')
        .replaceAll('&quot;', '')
        .replaceAll(',', '||')
      answer.push(listDataCorrect[i])
    }
    return answer
  }

  /**
   * Chuyển view nhập liệu trực quan cho học sinh
   * @param text
   * @author THNhat
   * @returns
   */
  convertViewBlankBox (text:string) {
    text = text.replaceAll('<span>&nbsp;</span>', '')
    const regBlankBox = /<span class="blank-box"([^>]{0,})><\/span>/g
    text = text.replace(regBlankBox, '<blank-box/>')
    return text.split('<blank-box/>')
  }

  /**
   * get đáp án của các câu hỏi blank box
   * @param text
   * @author THNhat
   * @returns list các object có 2 property là mentionName: string, mentionUserId: string
   */
  getNameMentioned (text:string) {
    const regMention = /<span class="mention"([^>]{0,})>/g
    const listMention = text.match(regMention) || []
    const res = []
    for (let i = 0; i < listMention?.length; i++) {
      const container = document.createElement('div')
      container.innerHTML = listMention[i]
      const mentionElement = container.querySelector('.mention')
      const mentionName = mentionElement?.getAttribute('data-mention')
      const mentionUserId = mentionElement?.getAttribute('data-user-id')
      const newMention = {
        mentionName: mentionName,
        mentionUserId: mentionUserId
      }
      res.push(newMention)
    }
    return res
  }

  /**
   * Tạo một mention với tên và id
   * @param name, id
   * @author THNhat
   * @returns
   */
  createMentionText (name:string, id: string) {
    return `<span class="mention" data-mention="${name}" data-user-id="${id}">${name}</span>&nbsp`
  }

  /**
   * @description chạy audio click
   * @author VDDung1
   */
  audioOnClick () {
    const audioClick = require('@/assets/audios/game/galactic-warrior/audio_clickmouse.mp3')
    const audioClickPlay = new Audio(audioClick)
    audioClickPlay.play()
  }

  /**
     * @description chuẩn hóa html
     * @author VDDung1
     */
  replaceHtmlTag (element : string) {
    return element
      .trim()
      .toLowerCase()
      .replace(/(<([^>]+)>)/gi, '')
      .replace(/\s+/g, ' ')
  }

  /**
   * Lấy danh sách năm học
   * NVDUC
   * @returns
   */
  getSchoolYearData () {
    return localStorage.get(localStorageKeys.SCHOOL_YEAR_DATA)
  }

  /**
   * @description chuẩn hóa string data
   * @author VDDung1
   */
  replaceStringData (element : string) {
    const me = this
    return me.replaceHtmlTag(element).replaceAll(' ', '')
  }

  /**
   * @descrtiion Lấy các thẻ quà tặng hiện tại
   * @author NTQuy
   * @param giftCardType
   * @returns
   */
  getCurrentGiftCard (giftCardType: number) {
    if (typeof (giftCardType) === 'number') {
      const giftCards : Array<any> = sessionStorage.get(sessionStorageKeys.TEACHER_GIFT_CARD)
      let currentGiftCard = null

      if (giftCards?.length) {
        currentGiftCard = giftCards.find(gifCard => gifCard.GiftCardType === giftCardType)
      }
      if (currentGiftCard) {
        return currentGiftCard
      }
    }
  }

  /**
   * @description Get current Config trong index DB
   * @author NTQuy
   * @returns
   */
  async getCurrentRankingConfig (stepRank: number = 0) {
    const currentTeacherRanking : any = sessionStorage.get(sessionStorageKeys.TEACHER_RANKING)
    const curentRanking = currentTeacherRanking?.Ranking
    // const currentRanking = 1
    let rankingConfig = 0
    if (typeof (curentRanking) === 'number') {
      let rank = curentRanking
      rank += stepRank
      rankingConfig = await TeacherRankingConfig.GetCurrentTeacherRankingConfig(rank)
    }

    return rankingConfig
  }

  /**
   * @description Lấy tất cả config ranking của teacher
   * @author NTQuy
   */
  async getTeacherRankingConfig () {
    return await TeacherRankingConfig.GetTeacherRankingConfig()
  }

  /**
   * Định dạng tiền
   */
  moneyFormat (money:string) {
    return money.split('').reverse().reduce((prev, next, index) => {
      return ((index % 3) ? next : (next + '.')) + prev
    })
  }

  /**
   * Lấy file Extension theo file name
   */
  getFileExtension (fileName: string) {
    const parts = fileName?.split('.')
    let extensionFile = parts.length > 1 ? parts.pop() : ''
    extensionFile = '.' + extensionFile?.toLowerCase()

    return extensionFile
  }
}

export default new CommonFn()
export {
  getI18n,
  focusFirstControl,
  handleNetworkError,
  redirectToApp,
  getLinkToApp,
  isTablet,
  destrucSchoolLevel,
  getCookie,
  setCookie
}

export type { CommonFn }
